import type { ReactNode, JSX } from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import type { CommonProps } from '../../types';
import { MobilityTypesUnion } from '@beat-venues/common-types';
import {
    BikeSolidIcon,
    BusIcon,
    CarSolidIcon,
    Paragraph,
    ParkRideSolidIcon,
    TrainSolidIcon,
    TramSolidIcon,
    Text,
} from '../../atoms';

export interface MobilityUpdatesListItemProps
    extends CommonProps,
        VariantProps<typeof MobilityUpdatesListItemStyles> {
    type: MobilityTypesUnion;
    publishedAt: string;
    description: string;
}

export const MobilityUpdatesListItemStyles = tv({
    slots: {
        root: 'flex gap-3 border-l last:border-0 border-gray-300 pl-6 relative pb-3',
        icon: 'bg-gray-300 rounded-full p-1 self-start flex items-center w-6 h-6 justify-center absolute left-0 top-0 -translate-x-1/2',
    },
    variants: {},
});

export const MobilityUpdatesListItem = ({
    className,
    type,
    publishedAt,
    description,
    testId = 'mobility-updates-list-item',
    ...props
}: MobilityUpdatesListItemProps): JSX.Element => {
    const { root, icon } = MobilityUpdatesListItemStyles();

    const embedMap = {
        bike: BikeSolidIcon,
        ov: TramSolidIcon,
        'event-shuttle': BusIcon,
        pr: ParkRideSolidIcon,
        train: TrainSolidIcon,
        car: CarSolidIcon,
    };

    const Component = embedMap[type];

    return (
        <li className={root({ className })} {...props} data-testid={testId}>
            <div className={icon()}>
                <Component />
            </div>
            <div>
                <Text className="font-normal">{publishedAt}</Text>
                <Paragraph>{description}</Paragraph>
            </div>
        </li>
    );
};

export interface MobilityUpdatesListProps
    extends CommonProps,
        VariantProps<typeof MobilityUpdatesListStyles> {
    children?: ReactNode;
}

export const MobilityUpdatesListStyles = tv({
    slots: {
        root: '',
    },
    variants: {},
});

const MobilityUpdatesList = ({
    className,
    children,
    testId = 'mobility-updates-list',
    ...props
}: MobilityUpdatesListProps): JSX.Element => {
    const { root } = MobilityUpdatesListStyles();
    return (
        <ul className={root({ className })} {...props} data-testid={testId}>
            {children}
        </ul>
    );
};

MobilityUpdatesList.Item = MobilityUpdatesListItem;

export { MobilityUpdatesList };
